<template lang="pug">
    div
        ExpandableCardListItem.mt-8.mb-4(
            :color="itemColor"
            expandable
            :is-expanded="showDetails === true"
            :content-right-top="resourceNote.user_name"
            :preview="resourceNote.content"
            :on-expand="() => showDetails = !showDetails"
            :flex-grow-right="5"
            :flex-grow-left="3"
            :title="resourceNote.name"
            :subtitle="shortenedDate(resourceNote.updated_at)"
        )               
            template(v-slot:body)
                v-card-text.white.text--primary(v-if="showDetails === true")
                    v-list.py-0
                        v-list-item
                            div.d-flex.flex-row.flex-grow-1
                                v-list-item-content
                                    v-list-item-title.font-weight-bold {{ resourceNote.name }}
                                    v-list-item-subtitle {{ resourceNote.user_name || 'Unknown Agent' }}
                                    v-list-item-subtitle.text-caption {{ formatDateTime(resourceNote.updated_at) }}
                                v-list-item-action
                                    v-tooltip(bottom)
                                        template(v-slot:activator="{ on, attrs }")
                                            v-btn.edit-icon(icon v-bind="attrs" v-on="on" @click="startEdit(resourceNote)")
                                                v-icon mdi-pencil
                                        span Edit Note
                        v-list-item(style="white-space: pre-line;")
                            | {{ resourceNote.content }}
                    v-divider
                    v-list-item
                        .d-flex.flex-row.flex-grow-1
                            v-list-item-title.font-weight-bold History
                            v-spacer
                            v-list-item-action
                                v-tooltip(v-if="!viewHistory" bottom)
                                    template(v-slot:activator="{on, attrs}")
                                        v-btn(v-bind="attrs" v-on="on" icon @click="showHistory()")
                                            v-icon mdi-eye
                                    span View Note History
                                v-tooltip(v-if="viewHistory" bottom)
                                    template(v-slot:activator="{on, attrs}")
                                        v-btn(v-if="viewHistory" v-on="on" icon @click="hideHistory()")
                                            v-icon mdi-eye-off
                                    span Hide History
                    v-list(v-if="viewHistory")
                        .text-center(v-if="historyLoading")
                            v-progress-circular(
                                :size='100'
                                :width='10'
                                color='#0f5b9a'
                                indeterminate
                                style="margin-top: 100px;"
                            )
                        template(v-else-if="noteVersions.length > 0" v-for="(version, idx) in noteVersions")
                            v-divider(v-if="idx > 0")
                            v-list(:key="version.key")
                                div.d-flex.flex-column.w-100
                                    v-list-item-title.w-100.text-right.d-flex.flex-column
                                        | {{ version.agent }}
                                        v-list-item-subtitle {{ version.date }}
                                    v-list-item.mt-4.text-wrap
                                        span(style="white-space: pre-line;") {{ version.content }}
                        template(v-else)
                            v-alert(outlined type="warning" :icon="false" width="100%")
                                | No history available
</template>

<script lang="ts">
    import Vue from 'vue';

    // Components
    import ExpandableCardListItem from '@/components/shared/expandable_card_list_item.vue';
    import ListItem from '@/components/shared/list_item.vue';

    // Data
    import { GetVersions } from "@/data";

    // Helpers
    import { resourceColorClass, } from "@/helpers/css_classes";


    export default Vue.extend({
        data() {
            return {
                showDetails: false,
                hideUpdateButton: false,
                historyLoading: false,
                viewHistory: false,
                noteVersions: [],
                historyFetched: false,
                mostRecentVersion: {},
            }
        },
        components: {
            ExpandableCardListItem,
            ListItem,
        },
        computed: {
            itemColor() {
                return resourceColorClass("Appointment");
            },
        },
        methods: {
            assembleVersionHistory(versions) {
                versions = versions.filter((version) => version.change_keys !== "{name}")
                versions.forEach((version, index) => {
                    let content = version.object.content
                    let agent = version.responsible_name || "Unknown Agent"
                    
                    let newVersion =  {
                        key: index,
                        content: content,
                        agent: agent,
                        date: version.friendly_created_at,
                    }
                    

                    this.noteVersions.push(newVersion);
                })
            },
            hideHistory() {
                this.viewHistory = false;
            },
            formatDateTime(date) {
                if (!date) return '';
                const d = new Date(date);
                return d.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
                });
            },
            shortenedDate(dateStr) {
                if (!dateStr) return '';
                const date = new Date(dateStr);
                return date.toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: 'numeric',
                });
            },
            getVersions() {
                if (!this.historyFetched) {
                    this.historyLoading = true;
                    let options = {
                        account_id: this.resourceNote.account_id,
                        campaign_id: 0,
                        last_version_id: 0,
                        limit: 20,
                        object_id: this.resourceNote.note_id,
                        object_type: "resource_note",
                        organization_id: this.resourceNote.organization_id,
                    }

                    GetVersions(options).then((response) => {
                        if (!response?.Data?.versions || response.Data.versions.length === 0) {
                            this.historyLoading = false;
                        } else {
                            this.assembleVersionHistory(response.Data.versions);
                            this.historyLoading = false;
                        }
                    }).finally(() => {
                        this.historyLoading = false;
                        this.historyFetched = true;
                    });
                }
            },
            showHistory() {
                if (!this.historyFetched) {
                    this.getVersions();
                }
                this.viewHistory = true;
            },
        },
        props: {
            resourceNote: {
                type: Object,
                required: true,
            },
            startEdit: Function,
            formatDate: {
                type: Function,
                required: true,
            }, 
        },
    });
</script>

<style lang="scss" scoped>
    .note-card {
        position: relative;
    }
    .absolute-bottom-right {
        position: absolute;
        right: 16px;
        bottom: 10px;
    }
    .note-content {
        font-size: 0.9rem;
        line-height: 1.2;
        white-space: pre-line;
    }
    .edit-icon {
        cursor: pointer;
        font-size: 24px;
        color: #1867c0;
        transition: color 0.2s;
    }
    .edit-icon:hover {
        color: #0d47a1;
    }
</style>